import React, { useMemo, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { CargoTypeAvatar } from "../../UpcomingPage/cargo-type-avatar";
import { useSelector } from "../../../redux-store";
import { NewNoteDot } from "./NoteDot";
import { formatCustomDateString } from "../../../helpers/date-helper";
import { useTranslation } from "react-i18next";
import { useL10n } from "../../../l10n";
import { useAppDispatch } from "../../../redux-store";
import * as dispatchingActions from "../../../ducks/app/dispatching";
import * as dispatchingSelectors from "../../../ducks/app/dispatching/selectors";
import { Identifier } from "typescript";
import * as cargoViewSelectors from "../../../ducks/data/cargo-views/selectors";
import { Stop, CargoView } from "../../../ducks/data/cargo-views/types";
import * as actions from "../../../ducks/app/dispatching";
import clsx from "clsx";
import { MuiTooltip } from "../../../Components/nav/nav-styles";
import { Check, MergeType, CallSplit } from "@mui/icons-material";
import {
  selectCargoTags,
  selectHideCargoDescription,
  selectHideCargoFromDriversDefaultSetting,
  selectHideInvoiceRef,
  selectHideLoadmeters,
  selectSplitCargoTeamId,
} from "../../../ducks/data/org-settings/selectors";
import { selectClient } from "../../../ducks/data/clients/selectors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleUser } from "@fortawesome/free-regular-svg-icons";
import Tooltip from "@mui/material/Tooltip";
import "./CargoCard.scss";
import CargoQualitiesAndDemandsTooltip from "../../../Components/CargoQualitiesAndDemandsTooltip";
import { DoraButton } from "dora-shared/frontend";
import SplitRouteOrCargoDialog from "../../../Components/modals/CreateEditShipmentDialog/SplitRouteOrCargoDialog";
import GenericPromptDialog from "../../../Components/modals/GenericPromptDialog/GenericPromptDialog";
import { useFeature } from "../../../hooks";
import { unsplitCargo } from "../../../ducks/data/split-cargo";
import { selectRouteForCargo } from "../../../ducks/data/route-views/selectors";
import SiteJobChip from "./SiteJobChip";

const getLocationText = (
  cargo: Stop | null,
  isWide: boolean | undefined
): string => {
  if (!cargo) return "";
  const contrPost = [cargo.countryCode, cargo.postcode]
    .filter((t) => !!t)
    .join("-");
  if (cargo.city && (isWide || !cargo.postcode)) {
    return contrPost ? contrPost + " " + cargo.city : cargo.city;
  }
  return contrPost;
};

const formatDateTime = (cargo: Stop | null, timeMinimised = false): string => {
  let d = cargo?.date ? formatCustomDateString(cargo?.date, "dd/MM") : "--/--";
  if (cargo?.time) {
    if (!timeMinimised) {
      d += ` (${cargo?.time})`;
    } else {
      d += ` (${cargo?.time.slice(0, 2)}:${cargo?.time.slice(3, 5)})`;
    }
  }
  return d;
};

interface Props {
  cargo: CargoView;
  onClick?: (cargoId: string) => void;
  // Only the undispatched list marks site job orders with their group; a card
  // that already sits on a route has nothing left to group.
  showSiteJob?: boolean;
}

type CargoIdProps = {
  cargoId: string;
  onClick?: (cargoId: string) => void;
};

type DraggableCargoProps = CargoIdProps & {
  routeId: string | null;
  isPlaceholder: boolean;
};
type DragItem = { cargoId: string; routeId: string | null };

export const CARGO_LIST_DRAGGABLE_CARGO = "CARGO_LIST_DRAGGABLE_CARGO";

export const DraggableCargo = (props: DraggableCargoProps) => {
  const { cargoId, routeId } = props;
  const cargo =
    useSelector(cargoViewSelectors.selectCargoView(props.cargoId)) || null;
  const cargosHiddenFromDriversDefault = useSelector(
    selectHideCargoFromDriversDefaultSetting
  );
  const dispatch = useAppDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const draggedCargoId = useSelector(
    (state) => dispatchingSelectors.selectDraggedCargoId(state) === cargoId
  );

  const [, drag] = useDrag(
    () => ({
      type: CARGO_LIST_DRAGGABLE_CARGO,
      item: () => {
        dispatch(dispatchingActions.beginDragCargo({ cargoId, routeId }));
        return { cargoId, routeId };
      },
      end: () => {
        dispatch(dispatchingActions.endDrag());
      },
    }),
    [cargoId, routeId]
  );

  const [, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >(() => {
    return {
      accept: [CARGO_LIST_DRAGGABLE_CARGO, "STOP"],
      drop: () => {
        dispatch(actions.commit());
      },
      hover: (item, monitor) => {
        return;
        // if (!ref.current) {
        //   return;
        // }
        // if (cargoId === item.cargoId) {
        //   return;
        // }
        // const clientOffset = monitor.getClientOffset();
        // if (clientOffset === null) {
        //   return;
        // }
        // const hoverBoundingRect = ref.current?.getBoundingClientRect();
        // const hoverMiddleY =
        //   (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
        // const hoverClientY = clientOffset.y - hoverBoundingRect.top;
        // const position = hoverClientY < hoverMiddleY ? "ABOVE" : "BELOW";
        // dispatch(
        //   dispatchingActions.hover({
        //     destRouteId: routeId,
        //     hoverCargoId: cargoId,
        //     position,
        //   })
        // );
      },
    };
  }, [cargoId]);

  drag(drop(ref));

  return (
    <div
      ref={ref}
      style={{
        ...(cargo?.operationInProgress
          ? {
              border: "2px solid orange",
              display: "none",
            }
          : cargosHiddenFromDriversDefault
            ? {
                border: cargo?.hideForDrivers ? "2px solid red" : "none",
              }
            : {
                opacity: cargo?.hideForDrivers ? 0.4 : draggedCargoId ? 0.5 : 1,
              }),
      }}
    >
      {cargo && !props.isPlaceholder ? (
        <CargoInfo
          cargo={cargo}
          onClick={props.onClick}
          showSiteJob={routeId === null}
        />
      ) : null}
    </div>
  );
};

export const CargoInfoId = (props: CargoIdProps) => {
  const cargo =
    useSelector(cargoViewSelectors.selectCargoView(props.cargoId)) || null;
  return cargo && <CargoInfo cargo={cargo} onClick={props.onClick} />;
};

const NewCargoInfoContents = React.memo(
  (
    props: Props & {
      dateInfo: string;
      dateInfoMinimised: string;
      dimensions: string;
      p: Stop | null;
      d: Stop | null;
    }
  ) => {
    const { t } = useTranslation(["dispatching", "split"]);
    const { cargo, dateInfo, dateInfoMinimised, dimensions, p, d } = props;
    const routeId = useSelector(selectRouteForCargo(cargo.id));
    const l10n = useL10n();
    const cargoTags = useSelector(selectCargoTags);
    const cargoTag = cargoTags.find((t) => t.id === cargo?.cargoTagId);
    const onClick = () => {
      props.onClick && props.onClick(props.cargo.id);
    };
    const client = useSelector(selectClient(cargo.clientId));
    const hideLoadmeters = useSelector(selectHideLoadmeters);
    const hideDescription = useSelector(selectHideCargoDescription);
    const hideInvoiceRef = useSelector(selectHideInvoiceRef);
    const splitTeamId = useSelector(selectSplitCargoTeamId);
    const liteVersion = useFeature("lite-version");
    const splitCargoButtonEnabled = useFeature("split-cargos-and-routes");
    const shouldShowSplitCargoButton = useMemo(
      () =>
        !liteVersion &&
        splitCargoButtonEnabled &&
        splitTeamId &&
        cargo.lastStop?.date,
      [liteVersion, splitCargoButtonEnabled, splitTeamId, cargo]
    );
    const [splitCargoDialogOpen, setSplitCargoDialogOpen] = useState(false);
    const [unsplitConfirmOpen, setUnsplitConfirmOpen] = useState(false);
    const [unsplitReqInProgress, setUnsplitReqInProgress] = useState(false);
    const dispatch = useAppDispatch();

    const longLocationInfo = useMemo(
      () =>
        `${getLocationText(p, true) || p?.place} - ${getLocationText(d, true) || d?.place || ""}`,
      [d, p]
    );

    const shortLocationInfo = useMemo(
      () =>
        `${getLocationText(p, false) || p?.place} - ${getLocationText(d, false) || d?.place || ""}`,
      [d, p]
    );

    const startOpeningTimes = useMemo(() => {
      return p?.openingTime ? `(${p?.openingTime})` : null;
    }, [p]);

    const stopOpeningTimes = useMemo(() => {
      return d?.openingTime ? `(${d?.openingTime})` : null;
    }, [d]);

    return (
      <div className="flex">
        {cargoTag ? (
          <Tooltip title={cargoTag.description} placement="top">
            <div
              className="cargo-card-border"
              style={{ borderLeftColor: cargoTag.color }}
            ></div>
          </Tooltip>
        ) : (
          <div className="cargo-card-border"></div>
        )}
        <div
          id={cargo.id} /* this is for UI tests */
          className={clsx("cargo-card", {
            "cargo-card--green": cargo.color === "GREEN",
            "cargo-card--yellow": cargo.color === "YELLOW",
          })}
          onClick={onClick}
        >
          <CargoQualitiesAndDemandsTooltip cargo={cargo}>
            <div className="cargo-card__icon">
              {cargo.type ? (
                <CargoTypeAvatar type={cargo.type} />
              ) : (
                <div style={{ width: "1.25rem" }}></div>
              )}
            </div>
          </CargoQualitiesAndDemandsTooltip>
          <div className="cargo-card__cities-short">{shortLocationInfo}</div>
          <div className="cargo-card__cities-long">
            <div>{longLocationInfo}</div>
          </div>
          <div className="cargo-card__dates">
            <span className="cargo-card-date--full">{dateInfo}</span>
            <span className={"cargo-card-date--min"}>{dateInfoMinimised}</span>
          </div>
          <div className="cargo-card__start-place">
            <div>{p?.place || ""}</div>
          </div>
          <div className="cargo-card__start-city">
            <div className="flex gap-1 overflow-hidden">
              {p?.city ? <span>{p.city}</span> : null}
              {startOpeningTimes && (
                <span className="cargo-card__opening-times--short">
                  {startOpeningTimes}
                </span>
              )}
            </div>
          </div>

          <div className="cargo-card__stop-place">
            <div>{d?.place || ""}</div>
          </div>
          <div className="cargo-card__stop-city">
            <div className="flex gap-1 overflow-hidden">
              {d?.city ? <span>{d?.city}</span> : null}
              {stopOpeningTimes && (
                <span className="cargo-card__opening-times--short">
                  {stopOpeningTimes}
                </span>
              )}
            </div>
          </div>
          <div className="cargo-card__right">
            <div className="cargo-card__dimensions">
              {props.showSiteJob && <SiteJobChip cargoId={cargo.id} />}
              {client ? (
                <div className="cargo-card__client">
                  <FontAwesomeIcon icon={faCircleUser} />
                  {client.client}
                </div>
              ) : (
                ""
              )}
              {!hideLoadmeters && (
                <div>
                  {l10n.formatNumber(cargo.totalLm)}&nbsp;
                  <span className="cargo-card__unit">
                    {t("cargoCard.loadmeterAbbreviation")}
                  </span>
                  {dimensions && (
                    <span className="cargo-card__unit">({dimensions})</span>
                  )}
                </div>
              )}
              {cargo.invoiceRef && !hideInvoiceRef && (
                <div className="cargo-card__invoice-ref">
                  {cargo.invoiceRef}
                </div>
              )}
            </div>
            {cargo.invoiced && (
              <div className="cargo-card__invoiced-icon flex gap-2 items-center">
                <MuiTooltip title={t("cargoCard.invoiced")} placement="top">
                  <Check />
                </MuiTooltip>
                {shouldShowSplitCargoButton && (
                  <div>
                    {cargo.hasBeenSplit ? (
                      <DoraButton
                        variant={"error"}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setUnsplitConfirmOpen(true);
                        }}
                        className="flex items-center gap-2"
                      >
                        <MergeType fontSize="small" />
                      </DoraButton>
                    ) : (
                      <DoraButton
                        variant={"primary"}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSplitCargoDialogOpen(true);
                        }}
                        className="flex items-center gap-2"
                      >
                        <CallSplit fontSize="small" />
                      </DoraButton>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          {!hideDescription && (
            <div className="cargo-card__bottom">{cargo.description}</div>
          )}
          {startOpeningTimes && (
            <div
              className="cargo-card__opening-times--long"
              style={{ gridColumn: 4 }}
            >
              {startOpeningTimes}
            </div>
          )}
          {stopOpeningTimes && (
            <div
              className="cargo-card__opening-times--long"
              style={{ gridColumn: 5 }}
            >
              {stopOpeningTimes}
            </div>
          )}
          <NewNoteDot className="cargo-card__note" note={cargo.note} />
          {splitCargoDialogOpen && (
            <SplitRouteOrCargoDialog
              cargosIds={[cargo.id]}
              routeId={routeId}
              onClose={() => setSplitCargoDialogOpen(false)}
            />
          )}
          {unsplitConfirmOpen && (
            <div onClick={(e) => e.stopPropagation()}>
              <GenericPromptDialog
                title={t("split:unsplit")}
                text={t("split:unsplitConfirmText")}
                continueText={t("split:unsplitConfirmButton")}
                continueButtonColor="error"
                buttonsDisabled={unsplitReqInProgress}
                onCancelClick={() => setUnsplitConfirmOpen(false)}
                onContinueClick={async () => {
                  setUnsplitReqInProgress(true);
                  try {
                    await dispatch(unsplitCargo(cargo.id));
                    setUnsplitConfirmOpen(false);
                  } finally {
                    setUnsplitReqInProgress(false);
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);

export const CargoInfo = (props: Props) => {
  const cargo = props.cargo;
  const p = cargo.firstStop;
  const d = cargo.lastStop;

  const onClick = () => {
    props.onClick && props.onClick(cargo.id);
  };

  const dimensions = useMemo(
    () =>
      cargo
        ? [cargo.length, cargo.width, cargo.height].filter((d) => !!d).join("x")
        : "",
    [cargo]
  );

  const dateInfo = useMemo(
    () => formatDateTime(p) + " - " + formatDateTime(d),
    [d, p]
  );

  const dateInfoMinimised = useMemo(
    () => formatDateTime(p, true) + " - " + formatDateTime(d, true),
    [d, p]
  );

  return (
    <NewCargoInfoContents
      cargo={cargo}
      showSiteJob={props.showSiteJob}
      dimensions={dimensions}
      dateInfo={dateInfo}
      dateInfoMinimised={dateInfoMinimised}
      onClick={onClick}
      p={p}
      d={d}
    />
  );
};
